// One description of each report, rendered two ways: as .xlsx sheets and as a
// printable A4 document. Both consumers read the SAME ReportDoc, so a column can
// never drift between what a teacher sees on paper and what they get in Excel.
//
// Column widths are Excel character units; print uses the same numbers as relative
// weights. Columns marked `wide` only appear in Excel — the learner × competency
// matrix is unreadable on A4 portrait, so print gets a compact summary instead.
import type { QuizRecord, SettingsRecord, StudentRecord, AnswerKeyRecord, ResultRecord } from '../db/schema.js';
import type { QuizAnalysis, ItemStat } from '../analysis/itemAnalysis.js';
import { difficultyBand, discriminationBand, distractorFindings, NON_FUNCTIONING_DISTRACTOR_RATE } from '../analysis/itemAnalysis.js';
import type { CompetencyGapReport } from '../analysis/competencyAnalysis.js';
import { leastMasteredFirst } from '../analysis/competencyAnalysis.js';
import { formatItemSpec } from '../analysis/competencyMap.js';
import { correctOptionFor } from '../scoring/score.js';

export type Align = 'left' | 'center' | 'right';

export interface ReportColumn {
  header: string;
  /** Excel character width; also the print column weight. */
  width: number;
  align?: Align;
  /** long free text — wraps in both outputs. */
  wrap?: boolean;
  /** Excel only. Dropped when printing (too wide for A4). */
  wide?: boolean;
}

export type ReportCell = string | number | null;

export interface ReportTable {
  /** section heading, shown above the table in print and in A1 of the sheet. */
  title: string;
  /** Excel sheet name (≤31 chars after sanitising). */
  sheetName: string;
  columns: ReportColumn[];
  rows: ReportCell[][];
  /** shown under the table in print, and as note rows in the sheet. */
  notes?: string[];
  /** hint for Excel page setup; print orientation is chosen per document. */
  landscape?: boolean;
  /** message rendered instead of the table when there are no rows. */
  emptyNote?: string;
}

export interface ReportDoc {
  /** e.g. 'Item/Competency Analysis' */
  title: string;
  /** header key/value pairs: School, Teacher, Grade & Section, Subject, … */
  meta: Array<[string, string]>;
  tables: ReportTable[];
  /** signature blocks printed at the foot of the document. */
  signatories: Array<{ label: string; name: string }>;
}

/** Everything the builders need that isn't in the analysis objects themselves. */
export interface ReportContext {
  quiz: QuizRecord;
  settings: SettingsRecord;
  /** class name, or 'All classes' for a rollup. */
  section: string;
  gradeLevel: string;
  /** date administered (ISO), blank for a rollup across dates. */
  date: string;
}

const dash = (v: string) => v || '—';

/**
 * "Grade 7 – Rizal" from a grade level and a class name — but class names in this
 * app are commonly typed as "Grade 9 – Rizal" already, so a name that starts with
 * its own grade is used as-is rather than prefixed twice.
 */
function gradeSection(gradeLevel: string, section: string): string {
  if (!section) return gradeLevel ? `Grade ${gradeLevel}` : '';
  if (/^grade\b/i.test(section.trim())) return section;
  return [gradeLevel ? `Grade ${gradeLevel}` : '', section].filter(Boolean).join(' – ');
}

/** Standard header block. Grade/section/subject come from the class, not settings. */
function metaFor(ctx: ReportContext, extra: Array<[string, string]> = []): Array<[string, string]> {
  const s = ctx.settings;
  return [
    ['School', dash(s.schoolName)],
    ...(s.schoolId ? ([['School ID', s.schoolId]] as Array<[string, string]>) : []),
    ...(s.division ? ([['Division', s.division]] as Array<[string, string]>) : []),
    ['Grade & Section', dash(gradeSection(ctx.gradeLevel, ctx.section))],
    ['Subject', dash(ctx.quiz.subject)],
    ['Assessment', dash(ctx.quiz.title)],
    ['Items', String(ctx.quiz.items)],
    ...(ctx.date ? ([['Date administered', ctx.date]] as Array<[string, string]>) : []),
    ['Teacher', dash(s.teacherName)],
    ...extra,
  ];
}

function signatoriesFor(s: SettingsRecord): Array<{ label: string; name: string }> {
  return [
    { label: s.teacherPosition || 'Teacher', name: s.teacherName },
    { label: s.principalPosition || 'School Principal', name: s.principalName },
  ];
}

// ---- Item/Competency Analysis -------------------------------------------

/**
 * Report #1 — per competency, least mastered first, plus the remediation roster
 * split by DepEd intervention tier and the separate guided-practice list.
 */
export function competencyDoc(report: CompetencyGapReport, ctx: ReportContext): ReportDoc {
  const ordered = leastMasteredFirst(report);
  const unrated = report.competencies.filter((c) => c.classPct == null);

  const summary: ReportTable = {
    title: 'Competency summary (least mastered first)',
    sheetName: 'Competency summary',
    columns: [
      { header: '', width: 6, align: 'center' },
      { header: 'Learning competency', width: 46, wrap: true },
      { header: 'Items', width: 12 },
      { header: 'Pts', width: 5, align: 'right' },
      { header: 'Class %', width: 8, align: 'right' },
      { header: 'Mastery level', width: 20 },
      { header: 'Class-level response', width: 30, wrap: true },
      { header: 'Mastered', width: 9, align: 'right' },
      { header: 'Needs remediation', width: 10, align: 'right' },
      { header: 'Guided practice', width: 9, align: 'right' },
    ],
    rows: [...ordered, ...unrated].map((c) => [
      c.label,
      c.description,
      formatItemSpec(c.items) || '—',
      c.possiblePerLearner || null,
      c.classPct,
      c.classPct == null ? 'not scored' : c.band,
      c.classPct == null ? '' : c.intervention.classResponse,
      c.classPct == null ? null : `${c.masteredCount}/${c.n}`,
      c.classPct == null ? null : c.needing.length,
      c.classPct == null ? null : c.guidedPractice.length,
    ]),
    notes: [
      `${report.cutoff}% is the passing mark. A competency below it is least mastered.`,
      `"Class-level response" applies to the class average. Individual learners can still be below ${report.cutoff}% when the class average passes — see the "Needs remediation" count and the remediation roster.`,
      `Competencies measured by fewer than ${report.minItemsForLearner} items are reported for the class only — too few items to judge one learner.`,
      ...(report.unmappedItems.length ? [`Items ${formatItemSpec(report.unmappedItems)} belong to no competency and are not in this report.`] : []),
    ],
  };

  const roster: ReportTable = {
    title: 'Learners needing remediation',
    sheetName: 'Remediation roster',
    columns: [
      { header: '', width: 6, align: 'center' },
      { header: 'Learning competency', width: 36, wrap: true },
      { header: 'LRN', width: 15 },
      { header: 'Learner', width: 26 },
      { header: 'Score', width: 8, align: 'center' },
      { header: '%', width: 6, align: 'right' },
      { header: 'DepEd descriptor', width: 24 },
      { header: 'Instructional response', width: 30, wrap: true },
    ],
    rows: report.competencies.flatMap((c) =>
      c.needing.map((l) => [
        c.label,
        c.description,
        l.lrn,
        l.name,
        `${l.earned}/${l.possible}`,
        l.pct,
        l.intervention.descriptor,
        l.intervention.response,
      ]),
    ),
    emptyNote: `No learner is below the ${report.cutoff}% passing mark on any competency.`,
    notes: ['Ordered by competency, then weakest learner first.'],
  };

  const guided: ReportTable = {
    title: `Learners for guided practice (passing, ${report.cutoff}–79%)`,
    sheetName: 'Guided practice',
    columns: [
      { header: '', width: 6, align: 'center' },
      { header: 'Learning competency', width: 40, wrap: true },
      { header: 'LRN', width: 15 },
      { header: 'Learner', width: 26 },
      { header: 'Score', width: 8, align: 'center' },
      { header: '%', width: 6, align: 'right' },
    ],
    rows: report.competencies.flatMap((c) =>
      c.guidedPractice.map((l) => [c.label, c.description, l.lrn, l.name, `${l.earned}/${l.possible}`, l.pct]),
    ),
    emptyNote: 'No learner is in the 75–79% band.',
    notes: ['Connecting / Natutungo — these learners passed. Guided practice for consistency, not remediation.'],
  };

  const items: ReportTable = {
    title: 'Per-item breakdown by competency',
    sheetName: 'Items by competency',
    columns: [
      { header: '', width: 6, align: 'center' },
      { header: 'Learning competency', width: 40, wrap: true },
      { header: 'Item', width: 6, align: 'center' },
      { header: 'Correct', width: 9, align: 'center' },
      { header: 'Item MPS', width: 9, align: 'right' },
      { header: `Below ${report.cutoff}%`, width: 9, align: 'center' },
    ],
    rows: report.competencies.flatMap((c) =>
      c.itemStats.map((it) => [c.label, c.description, it.q, `${it.correct}/${it.total}`, it.pct, it.pct < report.cutoff ? 'Yes' : '']),
    ),
    emptyNote: 'No scored items.',
  };

  const leastCount = ordered.filter((c) => !c.mastered).length;
  const needingAny = new Set(report.competencies.flatMap((c) => c.needing.map((l) => l.resultId))).size;

  return {
    title: 'Item/Competency Analysis',
    meta: metaFor(ctx, [
      ['Learners', String(report.n)],
      ['Competencies', String(report.competencies.length)],
      ['Least mastered', `${leastCount} of ${report.competencies.length}`],
      ['Needing remediation', `${needingAny} learner${needingAny === 1 ? '' : 's'}`],
      ['Passing mark', `${report.cutoff}%`],
    ]),
    tables: [summary, roster, guided, items],
    signatories: signatoriesFor(ctx.settings),
  };
}

// ---- per-learner mastery ------------------------------------------------

/**
 * Report #2 — one row per learner. Excel gets the full learner × competency
 * matrix; print drops the matrix columns (`wide`) and keeps the named summary,
 * which is what fits and what a teacher actually reads.
 */
export function learnerMasteryDoc(report: CompetencyGapReport, ctx: ReportContext): ReportDoc {
  const matrixCols: ReportColumn[] = report.competencies.map((c) => ({
    header: `${c.label} %`,
    width: 7,
    align: 'right',
    wide: true,
  }));

  const table: ReportTable = {
    title: 'Per learner — mastered & least mastered',
    sheetName: 'Per learner',
    landscape: true,
    columns: [
      { header: 'LRN', width: 15 },
      { header: 'Learner', width: 26 },
      { header: 'Score', width: 8, align: 'center' },
      { header: 'Overall %', width: 9, align: 'right' },
      ...matrixCols,
      { header: 'Mastered', width: 9, align: 'center' },
      { header: 'To work on', width: 10, align: 'center' },
      { header: 'Least mastered competency', width: 40, wrap: true },
      { header: 'Weakest %', width: 9, align: 'right' },
      { header: 'Instructional response', width: 30, wrap: true },
    ],
    rows: report.learners.map((l) => {
      const weakest = l.leastMastered[0];
      return [
        l.lrn,
        l.name,
        `${l.score}/${l.maxScore}`,
        l.overallPct,
        ...l.scores.map((s) => s.pct),
        `${l.mastered.length}/${l.scores.length}`,
        String(l.leastMastered.length),
        weakest ? weakest.description : '—',
        weakest ? weakest.pct : null,
        weakest ? weakest.intervention.response : 'All competencies at the passing mark',
      ];
    }),
    emptyNote: 'No scored papers yet.',
    notes: [
      'Least mastered competency = the learner\'s weakest; open the app for their full list.',
      `Per-competency percentage columns are in the Excel file; competencies with under ${report.minItemsForLearner} items are indicative only.`,
    ],
  };

  const legend: ReportTable = {
    title: 'Competency legend',
    sheetName: 'Legend',
    columns: [
      { header: '', width: 6, align: 'center' },
      { header: 'Learning competency', width: 60, wrap: true },
      { header: 'Cognitive level', width: 16 },
      { header: 'Items', width: 14 },
      { header: 'No. of items', width: 10, align: 'right' },
    ],
    rows: report.competencies.map((c) => [
      c.label,
      c.description,
      c.cognitiveLevel ?? '—',
      formatItemSpec(c.items) || '—',
      c.items.length,
    ]),
  };

  return {
    title: 'Per-learner Mastery Report',
    meta: metaFor(ctx, [
      ['Learners', String(report.n)],
      ['Competencies', String(report.competencies.length)],
      ['Passing mark', `${report.cutoff}%`],
    ]),
    tables: [table, legend],
    signatories: signatoriesFor(ctx.settings),
  };
}

// ---- item analysis + MPS ------------------------------------------------

/** The existing item-level analytics, as a printable/Excel document. */
export function itemAnalysisDoc(analysis: QuizAnalysis, ctx: ReportContext): ReportDoc {
  const num = (v: number | null) => (v == null ? null : Math.round(v * 100) / 100);

  const items: ReportTable = {
    title: 'Item analysis',
    sheetName: 'Item analysis',
    landscape: true,
    columns: [
      { header: 'Item', width: 5, align: 'center' },
      { header: 'Key', width: 5, align: 'center' },
      { header: 'Correct', width: 9, align: 'center' },
      { header: 'Upper 27%', width: 10, align: 'center' },
      { header: 'Lower 27%', width: 10, align: 'center' },
      { header: 'Diff. (p)', width: 8, align: 'right' },
      { header: 'Difficulty', width: 14 },
      { header: 'Decision', width: 9, align: 'center' },
      { header: 'Disc. (D)', width: 8, align: 'right' },
      { header: 'Disc. level', width: 10 },
      { header: 'Decision', width: 9, align: 'center' },
      { header: 'r_pb', width: 7, align: 'right' },
      { header: 'Blank', width: 6, align: 'right' },
      { header: 'Double', width: 7, align: 'right' },
      ...analysis.options.map((o) => ({ header: o, width: 5, align: 'right' as Align })),
    ],
    rows: analysis.items.map((it) => {
      const d = difficultyBand(it.difficulty);
      const disc = discriminationBand(it.discrimination);
      return [
        it.q,
        it.key,
        `${it.correct}/${it.total}`,
        it.upperCorrect == null ? '—' : `${it.upperCorrect}/${it.nUpper}`,
        it.lowerCorrect == null ? '—' : `${it.lowerCorrect}/${it.nLower}`,
        num(it.difficulty),
        d.level,
        d.decision,
        num(it.discrimination),
        disc.level,
        disc.decision ?? '—',
        num(it.pointBiserial),
        it.blank,
        it.double,
        ...analysis.options.map((o) => it.distractors[o] ?? 0),
      ];
    }),
    notes: [
      'Upper/Lower = correct in the top/bottom 27% by total score. Retain difficulty 0.41–0.60; retain discrimination 0.41+.',
      'A negative discrimination index means the key should be rechecked.',
      ...(analysis.statsAvailable ? [] : ['Discrimination, point-biserial and KR-20 need at least 10 learners with varied scores — shown as —.']),
      ...(analysis.reliableSample ? [] : [`Item statistics are less reliable below 30 learners (n=${analysis.n}).`]),
    ],
  };

  const mps: ReportTable = {
    title: 'Per-item MPS',
    sheetName: 'Per-item MPS',
    columns: [
      { header: 'Item', width: 6, align: 'center' },
      { header: 'Correct', width: 10, align: 'center' },
      { header: 'Item MPS', width: 10, align: 'right' },
      { header: 'Below 75%', width: 10, align: 'center' },
    ],
    rows: analysis.items.map((it) => {
      const pct = Math.round(it.difficulty * 100);
      return [it.q, `${it.correct}/${it.total}`, pct, pct < 75 ? 'Yes' : ''];
    }),
    notes: ['Items below 75% are not yet mastered by the class — the targets for remediation.'],
  };

  // The keyed option's count is marked so the answer is readable without
  // cross-referencing the Key column letter against the option headers.
  const optionCell = (it: ItemStat, o: string): ReportCell => {
    const c = it.distractors[o] ?? 0;
    return o === it.key ? `${c} ✓` : c;
  };

  const distractors: ReportTable = {
    title: 'Distractor Analysis',
    sheetName: 'Distractor analysis',
    columns: [
      { header: 'Item', width: 5, align: 'center' },
      { header: 'Key', width: 5, align: 'center' },
      { header: 'n', width: 5, align: 'right' },
      ...analysis.options.map((o) => ({ header: o, width: 6, align: 'right' as Align })),
      { header: 'Blank', width: 6, align: 'right' },
      { header: 'Double', width: 7, align: 'right' },
      { header: 'Diff. (p)', width: 8, align: 'right' },
      { header: 'Item flag', width: 13 },
      { header: 'Distractor findings', width: 30, wrap: true },
      // Excel only: these roughly double the column count and will not fit A4.
      { header: 'Upper n', width: 8, align: 'right', wide: true },
      { header: 'Lower n', width: 8, align: 'right', wide: true },
      ...analysis.options.flatMap((o) => [
        { header: `${o} upper`, width: 8, align: 'right' as Align, wide: true },
        { header: `${o} lower`, width: 8, align: 'right' as Align, wide: true },
      ]),
    ],
    rows: analysis.items.map((it) => [
      it.q,
      it.key,
      it.total,
      ...analysis.options.map((o) => optionCell(it, o)),
      it.blank,
      it.double,
      num(it.difficulty),
      it.flag ?? '',
      distractorFindings(it)?.join('; ') ?? '—',
      it.upperByOption == null ? '—' : it.nUpper,
      it.lowerByOption == null ? '—' : it.nLower,
      ...analysis.options.flatMap((o) => [
        it.upperByOption == null ? '—' : it.upperByOption[o] ?? 0,
        it.lowerByOption == null ? '—' : it.lowerByOption[o] ?? 0,
      ]),
    ]),
    notes: [
      '✓ marks the keyed answer; every other option is a distractor. Blanks and double-marks are counted separately and are never attributed to an option.',
      `Non-functioning distractor = chosen by fewer than ${Math.round(NON_FUNCTIONING_DISTRACTOR_RATE * 100)}% of examinees. This is a measurement-literature convention (Haladyna & Downing), not a DepEd rule.`,
      'Attracts top group = a distractor chosen by more upper-27% than lower-27% learners; it is the concrete explanation for a negative discrimination index.',
      ...(analysis.statsAvailable
        ? []
        : ['The upper/lower split and both distractor findings need at least 10 learners with varied scores — shown as —. The counts above are unaffected.']),
    ],
  };

  const dist: ReportTable = {
    title: 'Score distribution',
    sheetName: 'Score distribution',
    columns: [
      { header: 'Score', width: 8, align: 'center' },
      { header: 'Learners', width: 10, align: 'right' },
    ],
    rows: analysis.distribution.map((d) => [d.score, d.count]),
  };

  const bands: ReportTable = {
    title: 'Score Distribution by DepEd band',
    sheetName: 'Score band distribution',
    columns: [
      { header: 'Band', width: 10, align: 'center' },
      { header: 'DepEd descriptor', width: 26 },
      { header: 'Learners', width: 10, align: 'right' },
      { header: '% of class', width: 10, align: 'right' },
    ],
    rows: analysis.bandDistribution.map((b) => [b.range, b.descriptor || '—', b.count, b.pct]),
    notes: [
      'Bands are the DepEd SY 2026–2027 transition reference tiers. That reference table specifies them for COMPETENCY-level percentages; here they are applied to the overall quiz percentage — the same cut-offs, extended to a wider unit.',
      'Each learner\'s percentage is rounded to a whole number before it is banded, so the number shown is the number the band decision used.',
      'The 80–100 band carries no descriptor in the reference table.',
      `Includes the ${analysis.n} scored paper${analysis.n === 1 ? '' : 's'}.`,
    ],
  };

  return {
    title: 'Item Analysis & MPS Report',
    meta: metaFor(ctx, [
      ['Learners', String(analysis.n)],
      ['Mean score', `${analysis.meanScore} of ${analysis.maxScore}`],
      ['MPS', `${analysis.mps}%`],
      ['Mastery level', analysis.masteryLevel],
      ['Standard deviation', String(analysis.stdDev)],
      // Raw SD alone can't be compared across the nine presets, so the scale-free
      // form travels beside it rather than replacing it.
      ['SD as % of max', `${analysis.sdPctOfMax}%`],
      ['KR-20 reliability', analysis.kr20 == null ? '—' : String(analysis.kr20)],
      ['Highest / Lowest', `${analysis.highest} / ${analysis.lowest}`],
    ]),
    tables: [items, mps, distractors, dist, bands],
    signatories: signatoriesFor(ctx.settings),
  };
}

// ---- raw data ----------------------------------------------------------

/** Per-learner scores with the chosen option per item, plus the answer-key row. */
export function rawDataDoc(
  results: Array<{ result: ResultRecord; section: string; date: string }>,
  students: StudentRecord[],
  key: AnswerKeyRecord | null,
  ctx: ReportContext,
): ReportDoc {
  const byId = new Map(students.map((s) => [s.id, s]));
  const qs = Array.from({ length: ctx.quiz.items }, (_, i) => i + 1);

  const cell = (r: ResultRecord, q: number): string => {
    const a = r.rawAnswers.find((x) => x.q === q);
    if (!a) return '';
    if (a.flags.includes('double')) return '*';
    if (a.flags.includes('blank') || a.chosen === null) return '-';
    return a.chosen;
  };

  const rows: ReportCell[][] = results.map(({ result: r, section, date }) => {
    const stu = r.studentId ? byId.get(r.studentId) : undefined;
    const lrn = stu?.lrn ?? (/^\d{12}$/.test(r.idDigitsRead) ? r.idDigitsRead : r.capturedName ? '' : 'UNASSIGNED');
    return [
      lrn,
      stu?.name ?? r.capturedName ?? '',
      section,
      date,
      r.score,
      r.maxScore,
      r.maxScore ? Math.round((100 * (r.score ?? 0)) / r.maxScore) : null,
      ...qs.map((q) => cell(r, q)),
    ];
  });
  if (key) {
    rows.push(['ANSWER KEY', '', '', '', null, null, null, ...qs.map((q) => correctOptionFor(key, q) ?? '')]);
  }

  const table: ReportTable = {
    title: 'Scores and responses per learner',
    sheetName: 'Raw data',
    landscape: true,
    columns: [
      { header: 'LRN', width: 15 },
      { header: 'Learner', width: 26 },
      { header: 'Section', width: 16 },
      { header: 'Date', width: 12, align: 'center' },
      { header: 'Score', width: 7, align: 'right' },
      { header: 'Items', width: 7, align: 'right' },
      { header: '%', width: 6, align: 'right' },
      // One column per item: only Excel can carry these on a page.
      ...qs.map((q) => ({ header: `Q${q}`, width: 4, align: 'center' as Align, wide: true })),
    ],
    rows,
    emptyNote: 'No papers scanned yet.',
    notes: ['Per-item cells: the chosen option, “-” blank, “*” double-mark.', 'Item columns are in the Excel file.'],
  };

  return {
    title: 'Raw Scores & Responses',
    meta: metaFor(ctx, [['Papers', String(results.length)]]),
    tables: [table],
    signatories: signatoriesFor(ctx.settings),
  };
}

/** Columns that survive printing (the `wide` ones are Excel-only). */
export const printColumns = (t: ReportTable): number[] =>
  t.columns.map((c, i) => (c.wide ? -1 : i)).filter((i) => i >= 0);
